import { isArray } from 'lodash';
import {
  AudienceSetTargets,
  AudienceSetTargetsData,
  AudienceSetTargetType,
  Brand,
  DemographicsSet,
  GeolocationLocationSet,
  IGeolocationSet,
} from '../../types';
import { constantStringToHumanized } from '../stringModifier';
import { BusinessType } from './BrandHelpers';
import { DEFAULT_COLOR_THEME } from '../Styling';

export const EDUCATION_STATUSES: AudienceSetTargetsData[] = [
  { id: 1, name: 'High School', type: 'education_statuses' },
  { id: 2, name: 'Undergrad', type: 'education_statuses' },
  { id: 3, name: 'Alum', type: 'education_statuses' },
  { id: 4, name: 'High School Graduate', type: 'education_statuses' },
  { id: 5, name: 'Some College', type: 'education_statuses' },
  { id: 6, name: 'Associate Degree', type: 'education_statuses' },
  { id: 7, name: 'In Grad School', type: 'education_statuses' },
  { id: 8, name: 'Some Grad School', type: 'education_statuses' },
  { id: 9, name: 'Master Degree', type: 'education_statuses' },
  { id: 10, name: 'Professional Degree', type: 'education_statuses' },
  { id: 11, name: 'Doctorate Degree', type: 'education_statuses' },
  { id: 12, name: 'Unspecified', type: 'education_statuses' },
  { id: 13, name: 'Some High School', type: 'education_statuses' },
];

export const RELATIONSHIP_STATUSES: AudienceSetTargetsData[] = [
  { id: 1, name: 'Single', type: 'relationship_statuses' },
  { id: 2, name: 'In Relationship', type: 'relationship_statuses' },
  { id: 3, name: 'Married', type: 'relationship_statuses' },
  { id: 4, name: 'Engaged', type: 'relationship_statuses' },
  { id: 6, name: 'Not Specified', type: 'relationship_statuses' },
];

export const TARGET_TYPES: any[] = [
  { label: 'Interests', type: 'interests', class: 'adinterest' },
  {
    label: 'Behaviors',
    type: 'behaviors',
    class: 'adTargetingCategory',
  },
  { label: 'Life Events', type: 'life_events', class: 'adTargetingCategory' },
  { label: 'Industries', type: 'industries', class: 'adTargetingCategory' },
  { label: 'Income', type: 'income', class: 'adTargetingCategory' },
  {
    label: 'Family Statuses',
    type: 'family_statuses',
    class: 'adTargetingCategory',
  },
  {
    label: 'Education Schools',
    type: 'education_schools',
    class: 'adeducationschool',
  },
  {
    label: 'Education Majors',
    type: 'education_majors',
    class: 'adeducationmajor',
  },
  {
    label: 'Work Employers',
    type: 'work_employers',
    class: 'adworkemployer',
  },
  {
    label: 'Work Positions',
    type: 'work_positions',
    class: 'adworkposition',
  },
  {
    label: 'Education Statuses',
    type: 'education_statuses',
  },
  {
    label: 'Relationship Statuses',
    type: 'relationship_statuses',
  },
];

/**
 * Parses detailed targeting information from the provided audience set targets.
 *
 * @param {AudienceSetTargets} targets - The audience set targets to be parsed.
 * @returns {any} The parsed detailed targeting information.
 *
 * The function processes the following keys in the `targets` object:
 * - `exclusions`: Parses and includes exclusion targets.
 * - `flexible_spec`: Parses and includes flexible specification targets.
 * - `targeting_relaxation_types`, `targeting_automation`, `targeting_optimization`, `college_years`: Directly includes these keys without modification.
 * - Other keys: Parses and includes targets if they have a length greater than 0.
 *
 * The function uses `buildReachTargetingSpecs` to parse individual targeting specifications.
 */
export const parsedDetailedTargeting = (targets: AudienceSetTargets) => {
  let tempTargeting: any = {};
  Object?.keys(targets || {})?.forEach((key: string) => {
    if (key === 'exclusions') {
      Object?.keys(targets?.[key] || {})?.forEach((exclusionKey: string) => {
        if (
          targets?.[key]?.[exclusionKey]?.length === 0 ||
          !isArray(targets?.[key]?.[exclusionKey])
        )
          return;

        const parsedTargets = buildReachTargetingSpecs(
          targets[key],
          exclusionKey,
        );

        tempTargeting[key] = {
          ...tempTargeting[key],
          [exclusionKey]: parsedTargets,
        };
      });
    } else if (key === 'flexible_spec') {
      if (!targets?.flexible_spec?.length) return;

      targets?.flexible_spec?.forEach((flexible: AudienceSetTargetType) => {
        let tempFlexible: any = {};
        Object?.keys(flexible || {})?.forEach((flexibleKey: string) => {
          if (
            flexible?.[flexibleKey]?.length === 0 ||
            !isArray(flexible?.[flexibleKey])
          )
            return;

          const parsedTargets = buildReachTargetingSpecs(flexible, flexibleKey);

          tempFlexible = {
            ...tempFlexible,
            [flexibleKey]: parsedTargets,
          };
        });

        tempTargeting.flexible_spec = [
          ...(tempTargeting?.flexible_spec || []),
          tempFlexible,
        ];
      });
    } else if (
      [
        'targeting_relaxation_types',
        'targeting_automation',
        'targeting_optimization',
        'college_years',
      ].includes(key)
    ) {
      tempTargeting[key] = targets[key];
    } else {
      if (targets?.[key]?.length > 0) {
        tempTargeting[key] = buildReachTargetingSpecs(targets, key);
      }
    }
  });

  return tempTargeting;
};

/**
 * Builds reach targeting specifications based on the provided audience set targets and key.
 *
 * @param {AudienceSetTargets} targets - The audience set targets containing various targeting data.
 * @param {string} key - The key to access specific targeting data within the audience set targets.
 * @returns {Array<{ id: number, name?: string } | number>} An array of targeting specifications.
 * If the key is 'education_statuses' or 'relationship_statuses', it returns an array of target IDs.
 * Otherwise, it returns an array of objects containing the target ID and name.
 */
const buildReachTargetingSpecs = (targets: AudienceSetTargets, key: string) => {
  if (targets?.[key]?.length === 0 || !isArray(targets?.[key])) return [];

  return targets?.[key]?.map((target: AudienceSetTargetsData) => {
    if (['education_statuses', 'relationship_statuses'].includes(key)) {
      return target.id;
    }

    return { id: target.id, name: target.name };
  }, []);
};

export const humanizeLocationTitle = (type: string) => {
  if (type === 'geo_markets') return 'DMAs';

  if (type === 'custom_locations') return 'Addresses';

  return constantStringToHumanized(type);
};

export const isEmptyGeolocations = (geolocations: GeolocationLocationSet) => {
  return Object?.values(geolocations || {})?.every(
    (field) => Array.isArray(field) && field.length === 0,
  );
};

export const isEmptyTargets = (targets: AudienceSetTargets) => {
  return Object?.values(targets || {})?.every(
    (field) => Array.isArray(field) && field.length === 0,
  );
};

export const targetsToText = (targets: AudienceSetTargets) => {
  let text: string = '';

  try {
    Object?.keys(targets || {})?.forEach((key: string) => {
      if (key === 'exclusions') {
        Object?.keys(targets?.[key] || {})?.forEach((exclusionKey: string) => {
          if (
            targets?.[key]?.[exclusionKey]?.length === 0 ||
            !isArray(targets?.[key]?.[exclusionKey])
          ) {
            return;
          }

          text += `\nExcluded:\n${constantStringToHumanized(
            exclusionKey,
          )}: ${targets?.[key]?.[exclusionKey]
            ?.map((target: AudienceSetTargetsData) => target.name)
            ?.join(', ')}\n`;
        });
      } else if (key === 'flexible_spec') {
        if (!targets?.flexible_spec?.length) return;

        targets?.flexible_spec?.forEach(
          (flexible: AudienceSetTargetType, index: number) => {
            Object?.keys(flexible || {})?.forEach((flexibleKey: string) => {
              if (
                flexible?.[flexibleKey]?.length === 0 ||
                !isArray(flexible?.[flexibleKey])
              ) {
                return;
              }

              text += `${
                index === 0 ? 'Included' : 'and must also match'
              }:\n${constantStringToHumanized(flexibleKey)}: ${flexible?.[
                flexibleKey
              ]
                ?.map((target: AudienceSetTargetsData) => target.name)
                ?.join(', ')}\n`;
            });
          },
        );
      } else {
        if (targets?.[key]?.length > 0) {
          if (!isArray(targets?.[key])) return;

          text += `${constantStringToHumanized(key)}: ${targets?.[key]
            ?.map(
              (target: AudienceSetTargetsData) =>
                `${target.name} (${target.id})`,
            )
            ?.join(', ')}\n`;
        }
      }
    });

    return text;
  } catch (error: any) {
    console.log(error);
    return text;
  }
};

export const demographicsToText = (
  demographics: DemographicsSet,
  brand: Brand,
) => {
  let text: string = '';
  let data: DemographicsSet = demographics || {
    age_min: brand?.businessType === BusinessType.FRANDEV ? 25 : 18,
    age_max: brand?.businessType === BusinessType.FRANDEV ? 59 : 64,
    genders: [1, 2],
  };

  try {
    Object?.keys(data || {})?.forEach((key: string) => {
      if (key === 'age_min') {
        text += `${constantStringToHumanized(key)}: ${
          data?.[key] || brand?.businessType === BusinessType.FRANDEV ? 25 : 18
        }\n`;
      } else if (key === 'age_max') {
        text += `${constantStringToHumanized(key)}: ${
          data?.[key] || brand?.businessType === BusinessType.FRANDEV ? 59 : 64
        }\n`;
      } else if (key === 'genders') {
        text += `${constantStringToHumanized(key)}: ${
          data?.[key]?.length > 1
            ? 'All'
            : data?.[key]?.[0] === 1
            ? 'Male'
            : 'Female'
        }\n`;
      }
    });

    return text;
  } catch (error: any) {
    console.log(error);
    return text;
  }
};

export const geolocationToText = (geolocations: IGeolocationSet) => {
  let text: string = '';
  let data: IGeolocationSet = geolocations || {
    geolocations: {
      countries: [{ key: 'US', name: 'United States' }],
    },
  };
  try {
    Object?.keys(data || {})?.forEach((key: string) => {
      if (key === 'geolocations' || 'excludedGeolocations') {
        Object?.keys(data?.[key] || {})?.forEach((locationKey: string) => {
          if (
            data?.[key]?.[locationKey]?.length === 0 ||
            !isArray(data?.[key]?.[locationKey])
          ) {
            return;
          }

          text += `${
            key === 'geolocations' ? 'Included' : `\nExcluded`
          }:\n${humanizeLocationTitle(locationKey)}: ${data?.[key]?.[
            locationKey
          ]
            ?.map((location: GeolocationLocationSet) => location.name)
            ?.join(', ')}\n`;
        });
      }
    });

    return text;
  } catch (error: any) {
    console.log(error);
    return text;
  }
};

export const assetsSectionStyling = (isCreateAds = false) => {
  return {
    paddingBottom: '5px',
    minHeight: `calc(100vh - ${isCreateAds ? '250px' : '320px'})`,
    maxHeight: `calc(100vh - ${isCreateAds ? '250px' : '320px'})`,
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '10px',
    },
    '&::-webkit-scrollbar-track': {
      borderTopRightRadius: '5px',
      borderBottomRightRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: DEFAULT_COLOR_THEME,
      borderRadius: '18px',

      '&:hover': {
        background: DEFAULT_COLOR_THEME,
      },
    },
  };
};

export const getLocationFieldName = (type: string) => {
  if (!type) return 'custom_locations';

  if (
    [
      'city',
      'neighborhood',
      'subcity',
      'small_geo_area',
      'medium_geo_area',
      'large_geo_area',
    ].includes(type)
  ) {
    return 'cities';
  }

  if (type === 'geo_market') return 'geo_markets';

  if (type === 'zip') return 'zips';

  if (type === 'electoral_district') return 'electoral_districts';

  if (type === 'region') return 'regions';

  return 'countries';
};
